import { createImageThumbnail } from "./imageUpload";

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

const DEFAULT_TIMEOUT_MS = 45_000;
const SILENT_MEDIA_ERROR_HEADER = "x-silent-media-error";
let latestMediaAuthState: boolean | null = null;

function isMediaUpload(path: string, init: RequestInit): boolean {
  return path === "/api/app/media" && String(init.method ?? "GET").toUpperCase() === "POST";
}

function mediaFileDetails(init: RequestInit): string {
  if (!(init.body instanceof FormData)) return "";
  const value = init.body.get("file");
  if (!(value instanceof File)) return "";
  const type = value.type || "未設定";
  const sizeKb = Math.max(1, Math.round(value.size / 1024));
  return `\nファイル: ${value.name || "名称なし"}\n形式: ${type}\nサイズ: ${sizeKb} KB`;
}

function showMediaUploadError(message: string, init: RequestInit): void {
  window.alert(`写真を保存できませんでした。\n${message}${mediaFileDetails(init)}`);
}

function postMediaAuthState(authenticated: boolean): void {
  latestMediaAuthState = authenticated;
  navigator.serviceWorker?.controller?.postMessage({
    type: "media-auth",
    authenticated,
  });
  void navigator.serviceWorker?.ready.then((registration) => {
    if (latestMediaAuthState === null) return;
    registration.active?.postMessage({
      type: "media-auth",
      authenticated: latestMediaAuthState,
    });
  });
}

function syncMediaAuth(path: string, payload: unknown): void {
  if (path === "/api/auth/logout") {
    postMediaAuthState(false);
    return;
  }
  if (path === "/api/auth/login") {
    postMediaAuthState(true);
    return;
  }
  if (
    path === "/api/auth/session" &&
    payload !== null &&
    typeof payload === "object" &&
    "authenticated" in payload &&
    typeof payload.authenticated === "boolean"
  ) {
    postMediaAuthState(payload.authenticated);
  }
}

function mediaFileKey(payload: unknown): string {
  if (!payload || typeof payload !== "object" || !("media" in payload)) return "";
  const media = (payload as { media?: unknown }).media;
  if (!media || typeof media !== "object" || !("fileKey" in media)) return "";
  const value = (media as { fileKey?: unknown }).fileKey;
  return typeof value === "string" ? value : "";
}

function refreshMediaWorker(fileKey?: string): void {
  navigator.serviceWorker?.controller?.postMessage({
    type: "media-map-refresh",
    fileKey: fileKey || null,
  });
}

async function saveThumbnailForUpload(payload: unknown, init: RequestInit): Promise<void> {
  if (!(init.body instanceof FormData)) return;
  if (String(init.body.get("relatedType") ?? "") === "media-thumbnail") return;
  const originalFile = init.body.get("file");
  const category = String(init.body.get("category") ?? "");
  const originalFileKey = mediaFileKey(payload);
  if (!(originalFile instanceof File) || !category || !originalFileKey) return;

  const thumbnail = await createImageThumbnail(originalFile);
  if (!thumbnail) {
    refreshMediaWorker(originalFileKey);
    return;
  }

  const thumbnailForm = new FormData();
  thumbnailForm.set("file", thumbnail);
  thumbnailForm.set("filename", thumbnail.name);
  thumbnailForm.set("category", category);
  thumbnailForm.set("relatedType", "media-thumbnail");
  thumbnailForm.set("relatedId", originalFileKey);

  try {
    await api("/api/app/media", {
      method: "POST",
      headers: { [SILENT_MEDIA_ERROR_HEADER]: "1" },
      body: thumbnailForm,
    });
  } catch (error) {
    console.warn("thumbnail-upload-failed", error);
  } finally {
    refreshMediaWorker(originalFileKey);
  }
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const headers = new Headers(init.headers);
  const silentMediaError = headers.get(SILENT_MEDIA_ERROR_HEADER) === "1";
  headers.delete(SILENT_MEDIA_ERROR_HEADER);
  if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const mediaUpload = isMediaUpload(path, init);
  const controller = new AbortController();
  const upstreamSignal = init.signal;
  const abortFromUpstream = () => controller.abort();
  if (upstreamSignal) {
    if (upstreamSignal.aborted) controller.abort();
    else upstreamSignal.addEventListener("abort", abortFromUpstream, { once: true });
  }
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(path, {
      ...init,
      headers,
      credentials: "same-origin",
      signal: controller.signal,
    });

    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const serverMessage =
        typeof payload === "object" &&
        payload !== null &&
        "error" in payload &&
        typeof payload.error === "string"
          ? payload.error
          : "通信に失敗しました。";
      throw new ApiError(`${serverMessage}（HTTP ${response.status}）`, response.status, payload);
    }

    syncMediaAuth(path, payload);
    if (mediaUpload && !silentMediaError) {
      await saveThumbnailForUpload(payload, init);
    }
    return payload as T;
  } catch (error) {
    let finalError = error;
    if (controller.signal.aborted && !upstreamSignal?.aborted) {
      finalError = new ApiError(
        "通信が45秒以内に完了しませんでした。画面の「更新」で保存結果を確認してから、写真だけ再送してください。（HTTP 408）",
        408,
        null,
      );
    }

    if (mediaUpload && !silentMediaError) {
      const message = finalError instanceof Error && finalError.message
        ? finalError.message
        : "原因不明の通信エラーです。";
      showMediaUploadError(message, init);
    }
    throw finalError;
  } finally {
    window.clearTimeout(timeoutId);
    upstreamSignal?.removeEventListener("abort", abortFromUpstream);
  }
}
